import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import { smoothScrollTo, getLenis } from "@/hooks/useSmoothScroll";

// Hungarian number formatting: "180 000" with a thin/regular space (we use a
// regular space — matches the style of the spec sheet values like "50.000 Ft").
function formatNumber(n, lang) {
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n);
  return new Intl.NumberFormat(lang === "hu" ? "hu-HU" : "en-US").format(rounded);
}

let _uid = 0;
const newSurface = () => ({ id: ++_uid, width: "", height: "" });

export default function CostEstimator({ open, product, onClose }) {
  const { lang, t } = useLang();
  const e = t("products.estimator");
  const [surfaces, setSurfaces] = useState([newSurface()]);

  // Reset state every time the modal opens for a different product.
  useEffect(() => {
    if (open) {
      _uid = 0;
      setSurfaces([newSurface()]);
    }
  }, [open, product?.id]);

  // Lock body scroll while modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const prevBody = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Lenis runs its own scroll loop independent of CSS overflow, so we have
    // to explicitly stop it or the page keeps scrolling behind the modal.
    const lenis = getLenis();
    lenis?.stop();
    const onKey = (ev) => ev.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevBody;
      lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !product) return null;

  const { name, pricePerM2 } = product;

  const updateSurface = (id, key, value) => {
    // Allow only digits (max 4) — natural number cm input.
    const cleaned = String(value).replace(/\D+/g, "").slice(0, 4);
    setSurfaces((arr) => arr.map((s) => (s.id === id ? { ...s, [key]: cleaned } : s)));
  };
  const addSurface = () => setSurfaces((arr) => [...arr, newSurface()]);
  const removeSurface = (id) =>
    setSurfaces((arr) => (arr.length > 1 ? arr.filter((s) => s.id !== id) : arr));

  const areas = surfaces.map((s) => {
    const w = parseInt(s.width, 10);
    const h = parseInt(s.height, 10);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 0;
    return (w * h) / 10000; // cm² -> m²
  });
  const totalArea = areas.reduce((a, b) => a + b, 0);
  const totalPrice = totalArea * pricePerM2;
  const hasInput = totalArea > 0;

  const proceed = () => {
    // Build a structured, human-readable prefill message from the entered
    // surfaces. Includes a "Location: (please specify)" line that the visitor
    // is expected to fill in on the form. Skips empty/invalid surfaces.
    const p = e.prefill;
    const validSurfaces = surfaces
      .map((s, idx) => {
        const w = parseInt(s.width, 10);
        const h = parseInt(s.height, 10);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
        const m2 = (w * h) / 10000;
        const m2Str = m2.toLocaleString(lang === "hu" ? "hu-HU" : "en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return `• ${p.surface} #${idx + 1}: ${w} × ${h} ${e.cm} (${m2Str} m²)`;
      })
      .filter(Boolean);

    const totalAreaStr = totalArea.toLocaleString(
      lang === "hu" ? "hu-HU" : "en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    );
    const priceStr = `${formatNumber(totalPrice, lang)} ${p.currency}${p.from_suffix}`;
    const unitPriceStr = `${formatNumber(pricePerM2, lang)} ${p.currency} ${p.per_m2}`;

    const lines = [
      `${p.product}: ${name}`,
      `${p.unit_price}: ${unitPriceStr}`,
      "",
      `${p.surfaces}:`,
      ...validSurfaces,
      "",
      `${p.total_area}: ${totalAreaStr} m²`,
      `${p.estimated_price}: ${priceStr}`,
      "",
      `${p.location}: ${p.location_hint}`,
      "",
      `${p.notes}:`,
      "",
    ];
    const message = lines.join("\n");

    // Dispatch a global event Contact listens for; close estimator; scroll.
    window.dispatchEvent(
      new CustomEvent("privateglass:prefill-inquiry", {
        detail: {
          message,
          product_interest: name,
          product_id: product?.id,
          surfaces: surfaces
            .map((s) => ({
              width_cm: parseInt(s.width, 10) || 0,
              height_cm: parseInt(s.height, 10) || 0,
            }))
            .filter((s) => s.width_cm > 0 && s.height_cm > 0),
          total_area_m2: totalArea,
          estimated_price_huf: Math.round(totalPrice),
        },
      })
    );
    onClose?.();
    setTimeout(() => {
      smoothScrollTo(document.getElementById("contact"), { offset: -64 });
    }, 50);
  };

  return (
    <div
      data-testid="cost-estimator-modal"
      data-lenis-prevent
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={e.title}
    >
      <div
        data-testid="cost-estimator-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-[#1D1D1F]/55 backdrop-blur-md"
      />

      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.25)] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-8 md:px-10 pt-8 md:pt-10 pb-6 border-b border-black/5">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h3
                data-testid="cost-estimator-title"
                className="font-display font-light text-2xl md:text-3xl tracking-tight text-[#1D1D1F] leading-[1.1]"
              >
                {name}
              </h3>
              <p className="mt-1.5 text-[11px] font-body font-semibold tracking-[0.28em] uppercase text-[#86868B]">
                {e.title}
              </p>
              <p className="mt-4 font-body text-sm md:text-[15px] text-[#1D1D1F]/65 leading-relaxed max-w-md">
                {e.subtitle}
              </p>
              <p className="mt-3 font-body text-sm text-[#1D1D1F]/55">
                {e.unit_price_prefix}:{" "}
                <span className="font-medium text-[#1D1D1F]">
                  {formatNumber(pricePerM2, lang)}{" "}
                  {lang === "hu" ? "Ft" : "HUF"}
                </span>{" "}
                / m²
              </p>
            </div>
            <button
              data-testid="cost-estimator-close-x"
              onClick={onClose}
              aria-label={e.close}
              className="shrink-0 h-9 w-9 grid place-items-center rounded-full hover:bg-black/5 text-[#1D1D1F]/70 hover:text-[#1D1D1F] transition-colors text-xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Surface inputs */}
        <div
          data-lenis-prevent
          className="flex-1 overflow-y-auto overscroll-contain px-8 md:px-10 py-6 space-y-4"
        >
          {surfaces.map((s, idx) => {
            const area = areas[idx];
            return (
              <div
                key={s.id}
                data-testid={`cost-estimator-surface-${idx}`}
                className="bg-[#F5F5F7] rounded-2xl px-5 py-4 md:px-6 md:py-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-body font-semibold tracking-[0.28em] uppercase text-[#86868B]">
                    {e.surface_label} #{idx + 1}
                  </span>
                  {surfaces.length > 1 && (
                    <button
                      data-testid={`cost-estimator-remove-${idx}`}
                      onClick={() => removeSurface(s.id)}
                      aria-label={e.remove_surface}
                      className="h-7 w-7 grid place-items-center rounded-full hover:bg-black/10 text-[#1D1D1F]/60 hover:text-[#1D1D1F] transition-colors"
                    >
                      <span className="text-sm leading-none">−</span>
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-body text-[#1D1D1F]/55 mb-1.5">
                      {e.width} ({e.cm})
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      data-testid={`cost-estimator-width-${idx}`}
                      value={s.width}
                      onChange={(ev) => updateSurface(s.id, "width", ev.target.value)}
                      placeholder="120"
                      className="w-full bg-white rounded-xl px-4 py-3 font-body text-[#1D1D1F] placeholder:text-[#86868B] outline-none border border-transparent focus:border-[#1D1D1F]/20 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-body text-[#1D1D1F]/55 mb-1.5">
                      {e.height} ({e.cm})
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      data-testid={`cost-estimator-height-${idx}`}
                      value={s.height}
                      onChange={(ev) => updateSurface(s.id, "height", ev.target.value)}
                      placeholder="200"
                      className="w-full bg-white rounded-xl px-4 py-3 font-body text-[#1D1D1F] placeholder:text-[#86868B] outline-none border border-transparent focus:border-[#1D1D1F]/20 transition"
                    />
                  </div>
                </div>
                {area > 0 && (
                  <div className="mt-3 text-right text-[13px] font-body text-[#1D1D1F]/70">
                    {e.area}:{" "}
                    <span className="font-medium text-[#1D1D1F]">
                      {area.toLocaleString(lang === "hu" ? "hu-HU" : "en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      m²
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          <button
            data-testid="cost-estimator-add"
            onClick={addSurface}
            className="w-full bg-white border border-dashed border-[#1D1D1F]/20 hover:border-[#1D1D1F]/45 hover:bg-[#FAFAFA] text-[#1D1D1F]/70 hover:text-[#1D1D1F] rounded-2xl py-4 text-sm font-body font-medium transition-colors"
          >
            + {e.add_surface}
          </button>
        </div>

        {/* Totals & actions */}
        <div className="px-8 md:px-10 pt-6 pb-8 md:pb-10 border-t border-black/5">
          <div className="flex items-end justify-between gap-6 mb-6">
            <div>
              <p className="text-[11px] font-body font-semibold tracking-[0.28em] uppercase text-[#86868B]">
                {e.total_area}
              </p>
              <p
                data-testid="cost-estimator-total-area"
                className="mt-1 font-display font-light text-xl md:text-2xl text-[#1D1D1F]"
              >
                {totalArea.toLocaleString(lang === "hu" ? "hu-HU" : "en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                m²
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-body font-semibold tracking-[0.28em] uppercase text-[#86868B]">
                {e.total_price}
              </p>
              <p
                data-testid="cost-estimator-total-price"
                className="mt-1 font-display font-light text-2xl md:text-3xl text-[#1D1D1F] tracking-tight"
              >
                {hasInput ? (
                  <>
                    {formatNumber(totalPrice, lang)}{" "}
                    <span className="text-base md:text-lg font-body font-normal text-[#1D1D1F]/55">
                      {lang === "hu" ? "Ft" : "HUF"}
                      {e.from_suffix}
                    </span>
                  </>
                ) : (
                  <span className="text-sm md:text-base font-body text-[#1D1D1F]/45">
                    {e.empty_hint}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              data-testid="cost-estimator-close"
              onClick={onClose}
              className="inline-flex items-center justify-center bg-transparent hover:bg-black/5 text-[#1D1D1F] text-sm font-body font-medium rounded-full px-5 py-2.5 transition-colors"
            >
              {e.close}
            </button>
            <button
              data-testid="cost-estimator-proceed"
              onClick={proceed}
              className="inline-flex items-center justify-center bg-[#1D1D1F] hover:bg-black text-white text-sm font-body font-medium rounded-full px-6 py-2.5 transition-colors"
            >
              {e.proceed}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
